import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { onAuthStateChanged } from 'firebase/auth';
import { v4 as uuidv4 } from 'uuid';
import { auth } from '../../firebase';
import axios from '../../utils/axios';
import { withRequestDedupe } from '../requestDedupe';
import { showAlert } from './alertSlice';
import update from 'immutability-helper';
import { months } from '@/utils/constants';
import dayjs from '@/utils/dayjs';
import { detectTimezone } from 'utils/dateUtils';
import { createAuthedThunk } from '../authedThunk';

const dealsInitialState = {
  fetching: null,
  fetchingPagination: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  error: null,
  errorText: null,
  deals: [],
  emptyList: null,
  lastSeen: null,
  lastTime: 0,
};

const pinnedDealsInitialState = {
  fetching: null,
  fetchingPagination: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  error: null,
  errorText: null,
  deals: [],
  emptyList: null,
  lastSeen: null,
  lastTime: 0,
};

const actionPlanInitialState = {
  fetching: null,
  fetchingPagination: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  error: null,
  errorText: null,
  items: [],
  emptyList: null,
  lastSeen: null,
};

const actionHistoryInitialState = {
  fetching: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  error: null,
  errorText: null,
  items: [],
  emptyList: null,
};

const dealActivitiesInitialState = {
  fetching: null,
  fetchingPagination: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  error: null,
  errorText: null,
  dealActivities: null,
  emptyList: null,
  lastSeen: null,
};

const dealByIdInitialState = {
  fetching: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  error: null,
  errorText: null,
  deal: null,
};

const dealArtifactsInitialState = {
  fetching: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  error: null,
  errorText: null,
  dealArtifacts: null,
  timelineYears: [],
  timelineMonths: {},
};

const addDealArtifactInitialState = {
  processing: null,
  done: null,
  failed: null,
  uploadPercentage: {},
  uploadingFor: {},
  error: null,
  errorText: null,
};

const nbaOperationInitialState = {
  processing: null,
  done: null,
  failed: null,
  error: null,
  errorText: null,
};

const fetchDataInitialState = {
  fetching: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  error: null,
  errorText: null,
  data: null,
};

const initialState = {
  deals: { ...dealsInitialState },
  pinnedDeals: { ...pinnedDealsInitialState },
  actionPlan: { ...actionPlanInitialState },
  actionHistory: { ...actionHistoryInitialState },
  dealActivities: { ...dealActivitiesInitialState },
  dealArtifacts: { ...dealArtifactsInitialState },
  dealById: { ...dealByIdInitialState },
  addDealArtifactState: { ...addDealArtifactInitialState },
  addActionItem: { ...nbaOperationInitialState },
  updateNBA: { ...nbaOperationInitialState },
  updateBulkNBADueDate: { ...nbaOperationInitialState },
  generateNBA: { ...nbaOperationInitialState },
  nbaById: { ...fetchDataInitialState },
  interactionParticipants: { ...fetchDataInitialState },
  dealNames: { ...fetchDataInitialState },
  dealExistsInfo: { ...fetchDataInitialState },
  // Candidate actions
  archiveDeals: { ...nbaOperationInitialState },
  createNewDeal: { ...nbaOperationInitialState },
  deleteActivitiesForDeal: { ...nbaOperationInitialState },
  deleteActivityForDeal: { ...nbaOperationInitialState },
  deleteDeal: { ...nbaOperationInitialState },
  updateDeal: { ...nbaOperationInitialState },
  updateMeetingPrepData: { ...nbaOperationInitialState },
  requestNewCrm: { ...nbaOperationInitialState },
  crmDealById: { ...fetchDataInitialState },
  dealHighlightContainers: { ...fetchDataInitialState },
  dealHighlights: { ...fetchDataInitialState },
  dealMeetingPrep: { ...fetchDataInitialState },
  meetingPrepByLinkToken: { ...fetchDataInitialState },
  meetingPrepPublicLink: { ...fetchDataInitialState },
  dealsByIds: { ...fetchDataInitialState },
};

// --- Async Thunks ---

// Shared auth wrapper - see `redux/authedThunk`. Was a byte-identical
// local copy in each of eleven slices.
const thunk = createAuthedThunk;

export const toggleDealStarThunk = thunk(
  'deals/toggleDealStar',
  async (token, { dealId, status }) => {
    const r = await axios.post(`/us/qai/deals/${dealId}/star`, null, {
      params: { 'request-id': uuidv4(), status },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to toggle deal star');
  },
);

export const archiveDealsThunk = thunk(
  'deals/archiveDeals',
  async (token, payload) => {
    const r = await axios.put('/us/qai/deals/archive', payload, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to archive deals');
  },
);

export const createNewDealThunk = thunk(
  'deals/createNewDeal',
  async (token, payload) => {
    const r = await axios.post('/us/qai/deals', payload, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to create new deal');
  },
);

export const deleteActivitiesForDealThunk = thunk(
  'deals/deleteActivitiesForDeal',
  async (token, activityIds) => {
    const r = await axios.put(
      '/us/qai/deals/interactions/delete',
      activityIds,
      {
        params: { 'request-id': uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status === 200) return r.data;
    throw new Error('Failed to delete activities');
  },
);

export const deleteActivityForDealThunk = thunk(
  'deals/deleteActivityForDeal',
  async (token, activityId) => {
    const r = await axios.delete(`/us/qai/deals/interactions/${activityId}`, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to delete activity');
  },
);

export const deleteDealThunk = thunk(
  'deals/deleteDeal',
  async (token, dealId) => {
    const r = await axios.delete(`/us/qai/deals/${dealId}`, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to delete deal');
  },
);

export const fetchCrmDealById = thunk(
  'deals/fetchCrmDealById',
  async (token, crmDealId) => {
    const r = await axios.get(`/us/qai/crm/deals/${crmDealId}`, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to fetch CRM deal');
  },
);

/**
 * Resolves a list of deal ids into full deal objects. Built for the Reports
 * page's `DEALS_CLOSING_THIS_WEEK` report, whose `dealIds` the listing endpoint
 * returns raw. Ids with no matching deal in the org are silently dropped by the
 * API, so the response can be shorter than the request.
 */
export const fetchDealsByIds = thunk(
  'deals/fetchDealsByIds',
  async (token, dealIds) => {
    const ids = (dealIds || []).filter(Boolean);
    if (!ids.length) return [];
    const r = await axios.post('/us/qai/deals/by-ids', ids, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to fetch deals by ids');
  },
);

export const fetchDealHighlightContainers = thunk(
  'deals/fetchDealHighlightContainers',
  async (token, { dealId, signal }) => {
    const r = await axios.get(`/us/highlights/deals/${dealId}/containers`, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to fetch highlight containers');
  },
);

export const fetchDealHighlights = thunk(
  'deals/fetchDealHighlights',
  async (
    token,
    {
      dealId,
      highlightContainerId,
      lastSeen,
      limit,
      onlyForContainer,
      labels,
      signal,
    },
  ) => {
    const r = await axios.post(
      `/us/highlights/deals/${dealId}/highlights`,
      { labels },
      {
        params: {
          'request-id': uuidv4(),
          highlightContainerId,
          lastSeen,
          limit,
          onlyForContainer,
        },
        headers: { Authorization: `Bearer ${token}` },
        signal,
      },
    );
    if (r.status === 200) return r.data;
    throw new Error('Failed to fetch highlights');
  },
);

export const fetchDealMeetingPrep = thunk(
  'deals/fetchDealMeetingPrep',
  async (token, { dealId, statusOnly, nbaId, viewAsMode, signal }) => {
    const r = await axios.get(`/us/deals/${dealId}/meeting-prep`, {
      params: {
        'request-id': uuidv4(),
        goalStageStatusOnly: statusOnly,
        viewAsMode,
        nbaId,
      },
      headers: { Authorization: `Bearer ${token}`, 'x-api-version': 'v2' },
      signal,
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to fetch meeting prep');
  },
);

export const fetchMeetingPrepByLinkToken = createAsyncThunk(
  'deals/fetchMeetingPrepByLinkToken',
  async ({ linkToken, signal }, { rejectWithValue }) => {
    try {
      const r = await axios.get(`/us/link-token/${linkToken}/meeting-prep`, {
        params: { 'request-id': uuidv4() },
        signal,
      });
      if (r.status === 200) return r.data;
      return rejectWithValue('Failed to fetch meeting prep by link token');
    } catch (e) {
      return rejectWithValue(e?.response?.data || e.message);
    }
  },
);

export const fetchMeetingPrepPublicLink = thunk(
  'deals/fetchMeetingPrepPublicLink',
  async (token, { dealId, meetingPrepId, signal }) => {
    const r = await axios.get(
      `/us/deals/${dealId}/meeting-prep/${meetingPrepId}/deep-link`,
      {
        params: { 'request-id': uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
        signal,
      },
    );
    if (r.status === 200) return r.data;
    throw new Error('Failed to fetch meeting prep public link');
  },
);

export const updateDealThunk = thunk(
  'deals/updateDeal',
  async (token, { dealId, payload, version }) => {
    const r = await axios.put(`/us/qai/deals/${dealId}`, payload, {
      params: { 'request-id': uuidv4() },
      headers: {
        Authorization: `Bearer ${token}`,
        'x-api-version': version || 'v1',
      },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to update deal');
  },
);

export const updateMeetingPrepDataThunk = thunk(
  'deals/updateMeetingPrepData',
  async (token, { dealId, meetingPrepDataId, payload }) => {
    const r = await axios.put(
      `/us/deals/${dealId}/meeting-prep/${meetingPrepDataId}`,
      payload,
      {
        params: { 'request-id': uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status === 200) return r.data;
    throw new Error('Failed to update meeting prep data');
  },
);

export const requestNewCrmThunk = thunk(
  'deals/requestNewCrm',
  async (token, payload) => {
    const r = await axios.post('/us/new-crm-request', payload, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to request new CRM');
  },
);

// ... existing imports

export const addDealArtifact = createAsyncThunk(
  'deals/addDealArtifact',
  // `signal` (an AbortSignal) is optional: the meeting recorder aborts its
  // upload when the user leaves the page mid-way.
  async (
    {
      dealId,
      payload,
      audioData,
      callback,
      progressCallback,
      errCallback,
      signal,
    },
    { dispatch, rejectWithValue },
  ) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) {
            if (typeof errCallback === 'function')
              errCallback('User not authenticated');
            return reject(rejectWithValue('User not authenticated'));
          }
          try {
            const token = await user.getIdToken();
            const reqId = uuidv4();
            const response = await axios.post(
              `/us/qai/deals/interactions/v2`,
              payload,
              {
                params: {
                  'request-id': reqId,
                  dealId,
                },
                headers: {
                  Authorization: `Bearer ${token}`,
                },
                signal,
              },
            );

            if (response.status === 200) {
              const audioId = response?.data?.customField?.mediaFile?.fileId;
              const signedUrl = response?.data?.customField?.mediaFile?.url;

              if (signedUrl && audioData) {
                try {
                  if (typeof progressCallback === 'function')
                    progressCallback(audioId);

                  const gcpResponse = await axios.put(signedUrl, audioData, {
                    headers: {
                      'Content-Type': audioData?.type?.split(';')?.[0],
                    },
                    signal,
                    onUploadProgress: (progress) => {
                      dispatch(
                        setUploadingArtifactMediaFor({
                          audioId: audioId,
                          show: true,
                        }),
                      );
                      dispatch(
                        setUploadArtifactMediaProgress({
                          audioId: audioId,
                          percentage: Math.round(
                            (progress.loaded * 100) / progress.total,
                          ),
                        }),
                      );
                    },
                  });

                  if (gcpResponse.status === 200) {
                    setTimeout(() => {
                      dispatch(
                        setUploadingArtifactMediaFor({
                          audioId: audioId,
                          show: false,
                        }),
                      );
                    }, 2000);
                    dispatch(
                      setUploadArtifactMediaProgress({
                        audioId: audioId,
                        percentage: 100,
                      }),
                    );

                    const reqIdForConfirm = uuidv4();
                    const confirmVideoUploadResponse = await axios.get(
                      `/cs/sale-review/${audioId}/confirm`,
                      {
                        params: {
                          'request-id': reqIdForConfirm,
                        },
                        headers: {
                          Authorization: `Bearer ${token}`,
                        },
                        signal,
                      },
                    );

                    if (confirmVideoUploadResponse.status === 200) {
                      if (typeof callback === 'function')
                        callback(response?.data);
                      resolve(response.data);
                    } else {
                      if (typeof errCallback === 'function') errCallback();
                      reject(rejectWithValue('Failed to confirm upload'));
                    }
                  } else {
                    if (typeof errCallback === 'function') errCallback();
                    reject(rejectWithValue('Failed to upload to GCP'));
                  }
                } catch (e) {
                  if (typeof errCallback === 'function') errCallback();
                  reject(rejectWithValue(e.message));
                }
              } else {
                if (typeof callback === 'function') callback(response?.data);
                resolve(response.data);
              }
            } else {
              if (typeof errCallback === 'function') errCallback();
              reject(rejectWithValue('Failed to create artifact'));
            }
          } catch (e) {
            if (typeof errCallback === 'function') errCallback();
            reject(rejectWithValue(e.message));
          }
        },
        (error) => {
          if (typeof errCallback === 'function') errCallback();
          reject(rejectWithValue(error.message));
        },
      );
    });
  },
);

export const fetchDealById = createAsyncThunk(
  'deals/fetchDealById',
  // Up to four components on the deal page mount concurrently and each asks
  // for the deal; they share one request. See `redux/requestDedupe`.
  withRequestDedupe(
    (dealId) => (dealId ? `deal:${dealId}` : null),
    async (dealId, { rejectWithValue }) => {
      return new Promise((resolve, reject) => {
        const unsubscribe = onAuthStateChanged(
          auth,
          async (user) => {
            unsubscribe();
            if (!user) {
              return reject(rejectWithValue('User not authenticated'));
            }
            try {
              const token = await user.getIdToken();
              const reqId = uuidv4();
              const response = await axios.get(`/us/qai/deals/${dealId}`, {
                params: { 'request-id': reqId },
                headers: { Authorization: `Bearer ${token}` },
              });

              if (response.status === 200) {
                resolve(response.data);
              } else {
                reject(rejectWithValue('Failed to get deal by id'));
              }
            } catch (e) {
              reject(rejectWithValue('Failed to get deal by id'));
            }
          },
          (error) => {
            reject(rejectWithValue(error.message));
          },
        );
      });
    },
  ),
);

export const fetchDeals = createAsyncThunk(
  'deals/fetchDeals',
  // Two mounts request the same first page concurrently on /deals.
  withRequestDedupe(
    (payload) => `deals:${JSON.stringify(payload ?? null)}`,
    async (payload, { dispatch, rejectWithValue }) => {
      return new Promise((resolve, reject) => {
        const unsubscribe = onAuthStateChanged(
          auth,
          async (user) => {
            unsubscribe();
            if (!user) {
              return reject(rejectWithValue('User not authenticated'));
            }
            try {
              const token = await user.getIdToken();
              const reqId = uuidv4();
              const response = await axios.post(`/us/qai/deals/list`, payload, {
                params: {
                  'request-id': reqId,
                },
                headers: token ? { Authorization: `Bearer ${token}` } : {},
              });

              if (response.status === 200) {
                resolve({
                  data: response.data,
                  lastSeenArg: payload?.lastSeen || payload?.lastTime,
                  responseDataLastSeen:
                    response?.data?.[response?.data?.length - 1]?.id,
                });
              } else {
                console.log(response, 'response');

                dispatch(
                  showAlert({
                    message: 'Oops! Something went wrong. Please try again.',
                    showCross: true,
                    type: 'error',
                    autoHideDuration: 2000,
                  }),
                );
                reject(rejectWithValue('Failed to fetch deals'));
              }
            } catch (e) {
              console.log(e, 'error');

              dispatch(
                showAlert({
                  message: 'Oops! Something went wrong. Please try again.',
                  showCross: true,
                  type: 'error',
                  autoHideDuration: 2000,
                }),
              );
              reject(rejectWithValue('Failed to fetch deals'));
            }
          },
          (error) => {
            console.log(error, 'error');

            dispatch(
              showAlert({
                message: 'Oops! Something went wrong. Please try again.',
                showCross: true,
                type: 'error',
                autoHideDuration: 2000,
              }),
            );
            reject(rejectWithValue(error.message));
          },
        );
      });
    },
  ),
);

export const fetchPinnedDeals = createAsyncThunk(
  'deals/fetchPinnedDeals',
  async (payload, { dispatch, rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) {
            return reject(rejectWithValue('User not authenticated'));
          }
          try {
            const token = await user.getIdToken();
            const reqId = uuidv4();
            const response = await axios.get(`/us/qai/deals/starred`, {
              params: {
                'request-id': reqId,
                dealOwnerId: payload,
              },
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });

            if (response.status === 200) {
              resolve(response.data);
            } else {
              dispatch(
                showAlert({
                  message: 'Oops! Something went wrong. Please try again.',
                  showCross: true,
                  type: 'error',
                  autoHideDuration: 2000,
                }),
              );
              reject(rejectWithValue('Failed to fetch pinned deals'));
            }
          } catch (e) {
            dispatch(
              showAlert({
                message: 'Oops! Something went wrong. Please try again.',
                showCross: true,
                type: 'error',
                autoHideDuration: 2000,
              }),
            );
            reject(rejectWithValue('Failed to fetch pinned deals'));
          }
        },
        (error) => {
          dispatch(
            showAlert({
              message: 'Oops! Something went wrong. Please try again.',
              showCross: true,
              type: 'error',
              autoHideDuration: 2000,
            }),
          );
          reject(rejectWithValue(error.message));
        },
      );
    });
  },
);

export const fetchActionPlan = createAsyncThunk(
  'deals/fetchActionPlan',
  async ({ payload, version }, { dispatch, rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) {
            return reject(rejectWithValue('User not authenticated'));
          }
          try {
            const token = await user.getIdToken();
            const reqId = uuidv4();
            const response = await axios.post(`/us/qai/nba/overview`, payload, {
              params: {
                'request-id': reqId,
              },
              headers: token
                ? {
                    Authorization: `Bearer ${token}`,
                    'x-api-version': version || 'v1',
                  }
                : {},
            });

            if (response.status === 200) {
              resolve({
                items: response?.data?.data,
                lastSeen: response?.data?.lastSeen,
                lastSeenArg: payload?.lastSeen,
              });
            } else {
              dispatch(
                showAlert({
                  message: 'Oops! Something went wrong. Please try again.',
                  showCross: true,
                  type: 'error',
                  autoHideDuration: 2000,
                }),
              );
              reject(rejectWithValue('Failed to fetch action plan'));
            }
          } catch (e) {
            dispatch(
              showAlert({
                message: 'Oops! Something went wrong. Please try again.',
                showCross: true,
                type: 'error',
                autoHideDuration: 2000,
              }),
            );
            reject(rejectWithValue('Failed to fetch action plan'));
          }
        },
        (error) => {
          dispatch(
            showAlert({
              message: 'Oops! Something went wrong. Please try again.',
              showCross: true,
              type: 'error',
              autoHideDuration: 2000,
            }),
          );
          reject(rejectWithValue(error.message));
        },
      );
    });
  },
);

export const fetchActionHistory = createAsyncThunk(
  'deals/fetchActionHistory',
  async ({ payload }, { dispatch, rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) {
            return reject(rejectWithValue('User not authenticated'));
          }
          try {
            const token = await user.getIdToken();
            const reqId = uuidv4();
            const response = await axios.post(
              `/us/qai/nba/history`,
              payload || {},
              {
                params: { 'request-id': reqId },
                headers: token ? { Authorization: `Bearer ${token}` } : {},
              },
            );
            if (response.status === 200) {
              resolve({ items: response?.data || [] });
            } else {
              dispatch(
                showAlert({
                  message: 'Oops! Something went wrong. Please try again.',
                  showCross: true,
                  type: 'error',
                  autoHideDuration: 2000,
                }),
              );
              reject(rejectWithValue('Failed to fetch action history'));
            }
          } catch (e) {
            dispatch(
              showAlert({
                message: 'Oops! Something went wrong. Please try again.',
                showCross: true,
                type: 'error',
                autoHideDuration: 2000,
              }),
            );
            reject(rejectWithValue('Failed to fetch action history'));
          }
        },
        (error) => {
          dispatch(
            showAlert({
              message: 'Oops! Something went wrong. Please try again.',
              showCross: true,
              type: 'error',
              autoHideDuration: 2000,
            }),
          );
          reject(rejectWithValue(error.message));
        },
      );
    });
  },
);

export const fetchDealActivities = createAsyncThunk(
  'deals/fetchDealActivities',
  async ({ companyId, size, lastSeen }, { dispatch, rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) {
            return reject(rejectWithValue('User not authenticated'));
          }
          try {
            const token = await user.getIdToken();
            const reqId = uuidv4();
            const response = await axios.get(
              `/us/js/company-profile/${companyId}/deals`,
              {
                params: {
                  'request-id': reqId,
                  limit: size,
                  lastSeen,
                },
                headers: token ? { Authorization: `Bearer ${token}` } : {},
              },
            );

            if (response.status === 200) {
              resolve({
                data: response.data,
                lastSeenArg: lastSeen,
                responseDataLastSeen:
                  response?.data?.[response?.data?.length - 1]?.id,
              });
            } else {
              dispatch(
                showAlert({
                  message: 'Oops! Something went wrong. Please try again.',
                  showCross: true,
                  type: 'error',
                  autoHideDuration: 2000,
                }),
              );
              reject(rejectWithValue('Failed to fetch deal activities'));
            }
          } catch (e) {
            dispatch(
              showAlert({
                message: 'Oops! Something went wrong. Please try again.',
                showCross: true,
                type: 'error',
                autoHideDuration: 2000,
              }),
            );
            reject(rejectWithValue('Failed to fetch deal activities'));
          }
        },
        (error) => {
          dispatch(
            showAlert({
              message: 'Oops! Something went wrong. Please try again.',
              showCross: true,
              type: 'error',
              autoHideDuration: 2000,
            }),
          );
          reject(rejectWithValue(error.message));
        },
      );
    });
  },
);

export const fetchDealArtifacts = createAsyncThunk(
  'deals/fetchDealArtifacts',
  async ({ threadedView, dealId, signal }, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) {
            return reject(rejectWithValue('User not authenticated'));
          }
          try {
            const token = await user.getIdToken();
            const reqId = uuidv4();
            const response = await axios.get(
              `/us/qai/deals/${dealId}/interactions`,
              {
                params: {
                  requestId: reqId,
                  threadedView,
                  // interactionSource: 'meeting_recorder',
                },
                headers: {
                  Authorization: `Bearer ${token}`,
                },
                signal,
              },
            );

            if (response.status === 200) {
              resolve(response.data);
            } else {
              reject(rejectWithValue('Failed to fetch deal activities'));
            }
          } catch (e) {
            reject(rejectWithValue('Failed to fetch deal activities'));
          }
        },
        (error) => {
          reject(rejectWithValue(error.message));
        },
      );
    });
  },
);

export const deleteDealActivity = createAsyncThunk(
  'deals/deleteDealActivity',
  async (dealId, { dispatch, rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) {
            return reject(rejectWithValue('User not authenticated'));
          }
          try {
            const token = await user.getIdToken();
            const reqId = uuidv4();
            const response = await axios.delete(
              `/us/js/company-profile/deal/${dealId}`,
              {
                params: {
                  'request-id': reqId,
                },
                headers: token ? { Authorization: `Bearer ${token}` } : {},
              },
            );

            if (response.status === 200) {
              resolve(dealId);
            } else {
              dispatch(
                showAlert({
                  message: 'Failed to delete activity',
                  showCross: true,
                  type: 'error',
                  autoHideDuration: 2000,
                }),
              );
              reject(rejectWithValue('Failed to delete activity'));
            }
          } catch (e) {
            dispatch(
              showAlert({
                message: 'Failed to delete activity',
                showCross: true,
                type: 'error',
                autoHideDuration: 2000,
              }),
            );
            reject(rejectWithValue('Failed to delete activity'));
          }
        },
        (error) => {
          reject(rejectWithValue(error.message));
        },
      );
    });
  },
);

export const addActionItem = createAsyncThunk(
  'deals/addActionItem',
  async ({ dealId, interactionId, payload }, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.post(
              `/us/qai/deals/interactions/nba/add`,
              payload,
              {
                params: { requestId: uuidv4(), dealId, interactionId },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (response.status === 200) resolve(response.data);
            else reject(rejectWithValue('Failed to add action item'));
          } catch (e) {
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const updateNBA = createAsyncThunk(
  'deals/updateNBA',
  async ({ interactionId, actionId, payload }, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.put(
              `/us/qai/deals/interactions/nba/update/v2`,
              payload,
              {
                params: {
                  'request-id': uuidv4(),
                  nbaId: actionId,
                  interactionId,
                },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (response.status === 200) resolve(response.data);
            else reject(rejectWithValue('Failed to update NBA'));
          } catch (e) {
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const unarchiveNBAThunk = createAsyncThunk(
  'deals/unarchiveNBA',
  async (
    { interactionId, actionId, newDueDate },
    { dispatch, rejectWithValue },
  ) => {
    return dispatch(
      updateNBA({
        interactionId,
        actionId,
        payload: {
          deleted: false,
          done: false,
          archived: false,
          dueDateStr: dayjs(newDueDate).format('MM-DD-YYYY'),
          timezone: detectTimezone().timezone,
        },
      }),
    ).unwrap();
  },
);

export const updateBulkNBADueDate = createAsyncThunk(
  'deals/updateBulkNBADueDate',
  async (payload, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.put(
              `/us/qai/deals/interactions/nba/update/bulk`,
              payload,
              {
                params: { requestId: uuidv4() },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (response.status === 200) resolve(response.data);
            else reject(rejectWithValue('Failed to update bulk NBA due dates'));
          } catch (e) {
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const fetchNBAById = createAsyncThunk(
  'deals/fetchNBAById',
  async ({ interactionId, nbaId }, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.get(
              `/us/qai/deals/interactions/${interactionId}/nba/${nbaId}`,
              {
                params: { requestId: uuidv4() },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (response.status === 200) resolve(response.data);
            else reject(rejectWithValue('Failed to fetch NBA by id'));
          } catch (e) {
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const generateNBAThunk = createAsyncThunk(
  'deals/generateNBA',
  async ({ interactionId, payload }, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.post(
              `/us/qai/deals/interactions/${interactionId}/nba`,
              payload,
              {
                params: { requestId: uuidv4() },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (response.status === 200) resolve(response.data);
            else reject(rejectWithValue(response?.response?.data));
          } catch (e) {
            reject(rejectWithValue(e?.response?.data));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const deleteRestoreScheduledBot = createAsyncThunk(
  'deals/deleteRestoreScheduledBot',
  async ({ nbaId, action }, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.put(
              `/us/qai/nba/${nbaId}/meeting-bots`,
              {},
              {
                params: { 'request-id': uuidv4(), action },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (response.status === 200) resolve(response.data);
            else
              reject(rejectWithValue('Failed to delete/restore scheduled bot'));
          } catch (e) {
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const fetchAllInteractionParticipants = createAsyncThunk(
  'deals/fetchAllInteractionParticipants',
  async ({ dealId, interactionId }, { dispatch, rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.get(
              `/us/qai/deals/${dealId}/interactions/${interactionId}/stake-holder-and-seller-participants`,
              {
                params: { 'request-id': uuidv4() },
                headers: token ? { Authorization: `Bearer ${token}` } : {},
              },
            );
            if (response.status === 200) resolve(response.data);
            else {
              dispatch(
                showAlert({
                  message:
                    'Failed to get participants for activity. Please try again!',
                  showCross: true,
                  type: 'error',
                  autoHideDuration: 2000,
                }),
              );
              reject(
                rejectWithValue('Failed to fetch interaction participants'),
              );
            }
          } catch (e) {
            dispatch(
              showAlert({
                message:
                  'Failed to get participants for activity. Please try again!',
                showCross: true,
                type: 'error',
                autoHideDuration: 2000,
              }),
            );
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const fetchDealNames = createAsyncThunk(
  'deals/fetchDealNames',
  // Deals page and Ask AI both request the name list on mount.
  withRequestDedupe(
    (payload) => `dealNames:${JSON.stringify(payload ?? null)}`,
    async (payload, { rejectWithValue }) => {
      return new Promise((resolve, reject) => {
        const unsubscribe = onAuthStateChanged(
          auth,
          async (user) => {
            unsubscribe();
            if (!user) return reject(rejectWithValue('User not authenticated'));
            try {
              const token = await user.getIdToken();
              const response = await axios.get(`/us/qai/deals/list/names`, {
                params: { 'request-id': uuidv4(), ownerId: payload },
                headers: token ? { Authorization: `Bearer ${token}` } : {},
              });
              if (response.status === 200) resolve(response.data);
              else reject(rejectWithValue(response?.response?.data));
            } catch (e) {
              reject(rejectWithValue(e?.response?.data));
            }
          },
          (error) => reject(rejectWithValue(error.message)),
        );
      });
    },
  ),
);

export const fetchDealExistsInfo = createAsyncThunk(
  'deals/fetchDealExistsInfo',
  async (domain, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.post(
              `/us/qai/stakeholder/domains/deal`,
              { domain },
              {
                params: { 'request-id': uuidv4() },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (response.status === 200) resolve(response.data);
            else reject(rejectWithValue('Failed to fetch deal exists info'));
          } catch (e) {
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const deleteDealStakeholder = createAsyncThunk(
  'deals/deleteDealStakeholder',
  async ({ dealId, stakeholderId }, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.delete(
              `/us/qai/deals/${dealId}/stake-holder/${stakeholderId}`,
              {
                params: { 'request-id': uuidv4() },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (response.status === 200) resolve(stakeholderId);
            else reject(rejectWithValue('Failed to delete deal stakeholder'));
          } catch (e) {
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

const dealsSlice = createSlice({
  name: 'deals',
  initialState,
  reducers: {
    // Legacy reducers migrated
    resetDealsByIds: (state) => {
      state.dealsByIds = { ...fetchDataInitialState };
    },
    updateDeals: (state, action) => {
      const { id, data } = action.payload;
      state.deals.deals = state.deals.deals.map((sr) => {
        if (sr?.id === id) {
          return { ...sr, ...(data || {}) };
        }
        return sr;
      });
    },
    updatePinnedDeals: (state, action) => {
      const { id, data } = action.payload;
      state.pinnedDeals.deals = state.pinnedDeals.deals.map((sr) => {
        if (sr?.id === id) {
          return data;
        }
        return sr;
      });
    },
    removeDealFromPinnedDeals: (state, action) => {
      const { id } = action.payload;
      state.pinnedDeals.deals = state.pinnedDeals.deals.filter(
        (sr) => sr?.id !== id,
      );
    },
    addDealsToPinnedDeals: (state, action) => {
      state.pinnedDeals.deals = [
        ...(action.payload.deals || []),
        ...state.pinnedDeals.deals,
      ];
    },
    updateActionPlanItems: (state, action) => {
      const payload = action.payload;
      if (payload.changedDueDate) {
        state.actionPlan.items.forEach((sr) => {
          const itemDate = Object.keys(sr || {})?.[0];
          if (itemDate === payload.date) {
            if (sr[itemDate]?.[payload.dealId]?.length > 1) {
              sr[itemDate][payload.dealId] = sr[itemDate][
                payload.dealId
              ].filter(
                (interaction) => interaction?.nba?.id !== payload.actionId,
              );
            } else {
              delete sr[itemDate][payload.dealId];
            }
          } else if (itemDate === payload.changedDueDate) {
            sr[itemDate][payload.dealId] = [
              ...(sr[itemDate][payload.dealId] || []),
              payload.data,
            ];
          }
        });
      } else {
        state.actionPlan.items.forEach((sr) => {
          const itemDate = Object.keys(sr || {})?.[0];
          if (itemDate === payload.date) {
            sr[itemDate][payload.dealId] = sr[itemDate][
              payload.dealId
            ]?.map((interaction) =>
              interaction?.nba?.id === payload.actionId
                ? payload.data
                : interaction,
            );
          }
        });
      }
    },
    updateMultipleActionPlanItemsDueDate: (state, action) => {
      const payload = action.payload;
      state.actionPlan.items.forEach((sr) => {
        const itemDate = Object.keys(sr || {})?.[0];
        if (itemDate === payload.targetDate) {
          sr[itemDate][payload.dealId] = [
            ...(sr[itemDate][payload.dealId] || []),
            ...(payload.data || []),
          ];
        }
        if (itemDate === payload.date) {
          delete sr[itemDate][payload.dealId];
        }
      });
    },
    updateActionPlanItemsList: (state, action) => {
      const payload = action.payload;
      state.actionPlan.items.forEach((sr) => {
        const itemDate = Object.keys(sr || {})?.[0];
        if (itemDate === payload.date) {
          sr[itemDate][payload.dealId] = [
            ...(sr[itemDate][payload.dealId]?.filter(
              (item) => item?.nba?.id !== payload?.data?.[0]?.nba?.id,
            ) || []),
            ...(payload.data || []),
          ];
        }
      });
    },
    updateActionPlanOrder: (state, action) => {
      state.actionPlan.items = action.payload.items;
    },
    updateLastSeen: (state, action) => {
      state.actionPlan.lastSeen = action.payload;
    },
    addToActionHistory: (state, action) => {
      const item = action.payload;
      state.actionHistory.items = [item, ...(state.actionHistory.items || [])];
    },
    removeFromActionHistory: (state, action) => {
      const { nbaId } = action.payload;
      state.actionHistory.items = state.actionHistory.items.filter(
        (item) => item?.nba?.id !== nbaId,
      );
    },
    // deleteActivity: (state, action) => {
    //   state.dealActivities.dealActivities = state.dealActivities.dealActivities.filter(
    //     (ac) => ac?.id !== action.payload,
    //   );
    // },
    addingDealArtifact: (state) => {
      state.addDealArtifactState.processing = true;
    },
    setUploadArtifactMediaProgress: (state, action) => {
      state.addDealArtifactState.uploadPercentage = update(
        state.addDealArtifactState.uploadPercentage,
        {
          [action.payload.audioId]: { $set: action.payload.percentage },
        },
      );
    },
    setUploadingArtifactMediaFor: (state, action) => {
      state.addDealArtifactState.uploadingFor = update(
        state.addDealArtifactState.uploadingFor,
        {
          [action.payload.audioId]: { $set: action.payload.show },
        },
      );
    },
    addingDealArtifactDone: (state) => {
      state.addDealArtifactState.processing = false;
      state.addDealArtifactState.done = true;
      state.addDealArtifactState.failed = false;
      state.addDealArtifactState.error = false;
      state.addDealArtifactState.errorText = '';
    },
    addingDealArtifactFailed: (state, action) => {
      state.addDealArtifactState.processing = false;
      state.addDealArtifactState.done = false;
      state.addDealArtifactState.failed = true;
      state.addDealArtifactState.error = true;
      state.addDealArtifactState.errorText = action.payload;
    },
    updateDealById: (state, action) => {
      if (state.dealById.deal) {
        state.dealById.deal = {
          ...state.dealById.deal,
          ...action.payload,
        };
      }
    },
    // `updateDealById` replaces `company` wholesale; use this when only a few
    // company fields are known (async research status, for instance) so the rest
    // of the company details survive.
    updateDealCompany: (state, action) => {
      if (state.dealById.deal) {
        state.dealById.deal.company = {
          ...(state.dealById.deal.company || {}),
          ...action.payload,
        };
      }
    },
    updateDealActivity: (state, action) => {
      const { artifactId, threadId, updates } = action.payload;
      if (state.dealArtifacts.dealArtifacts) {
        state.dealArtifacts.dealArtifacts = state.dealArtifacts.dealArtifacts.map(
          (artifact) => {
            if (
              (artifactId && artifact?.id === artifactId) ||
              (threadId && artifact?.threadId === threadId)
            ) {
              return {
                ...artifact,
                ...updates,
                id: artifact.id,
              };
            }
            return artifact;
          },
        );
      }
    },
  },
  extraReducers: (builder) => {
    // --- Deals ---
    builder
      .addCase(fetchDeals.pending, (state, action) => {
        const { lastSeen, lastTime } = action.meta.arg || {};
        if (lastSeen || lastTime) {
          state.deals.fetching = false;
          state.deals.fetchingPagination = true;
        } else {
          state.deals.fetching = true;
          state.deals.fetchingPagination = false;
        }
      })
      .addCase(fetchDeals.fulfilled, (state, action) => {
        const { data, lastSeenArg, responseDataLastSeen } = action.payload;
        state.deals.fetching = false;
        state.deals.fetchingPagination = false;
        state.deals.fetchingSuccessful = true;
        state.deals.fetchingFailed = false;
        state.deals.error = false;
        state.deals.errorText = '';

        if (lastSeenArg) {
          state.deals.deals = Array.isArray(data)
            ? state.deals.deals
              ? state.deals.deals.concat(data)
              : data
            : state.deals.deals;

          state.deals.emptyList =
            Array.isArray(data) && (data.length === 0 || data.length % 10 > 0);
        } else {
          state.deals.deals = data;
          state.deals.emptyList = false;
        }
        // logic for lastSeen/lastTime
        state.deals.lastSeen = responseDataLastSeen || state.deals.lastSeen;
        state.deals.lastTime = responseDataLastSeen || state.deals.lastTime;
      })
      .addCase(fetchDeals.rejected, (state, action) => {
        state.deals.fetching = false;
        state.deals.fetchingSuccessful = false;
        state.deals.fetchingPagination = false;
        state.deals.fetchingFailed = true;
        state.deals.deals = [];
        state.deals.error = true;
        state.deals.errorText = action.payload || 'Failed';
      });

    // --- Pinned Deals ---
    builder
      .addCase(fetchPinnedDeals.pending, (state) => {
        state.pinnedDeals.fetching = true;
        state.pinnedDeals.fetchingPagination = false;
      })
      .addCase(fetchPinnedDeals.fulfilled, (state, action) => {
        const data = action.payload;
        state.pinnedDeals.fetching = false;
        state.pinnedDeals.fetchingPagination = false;
        state.pinnedDeals.fetchingSuccessful = true;
        state.pinnedDeals.fetchingFailed = false;
        state.pinnedDeals.error = false;
        state.pinnedDeals.errorText = '';

        state.pinnedDeals.deals = Array.isArray(data) ? data : [];
        state.pinnedDeals.emptyList = !Array.isArray(data) || data.length === 0;
      })
      .addCase(fetchPinnedDeals.rejected, (state, action) => {
        state.pinnedDeals.fetching = false;
        state.pinnedDeals.fetchingSuccessful = false;
        state.pinnedDeals.fetchingPagination = false;
        state.pinnedDeals.fetchingFailed = true;
        state.pinnedDeals.error = true;
        state.pinnedDeals.errorText = action.payload || 'Failed';
      });

    // --- Action Plan ---
    builder
      .addCase(fetchActionPlan.pending, (state, action) => {
        const { payload } = action.meta.arg;
        if (payload?.lastSeen) {
          state.actionPlan.fetching = false;
          state.actionPlan.fetchingPagination = true;
        } else {
          state.actionPlan.fetching = true;
          state.actionPlan.fetchingPagination = false;
        }
      })
      .addCase(fetchActionPlan.fulfilled, (state, action) => {
        const { items, lastSeen, lastSeenArg } = action.payload;
        state.actionPlan.fetching = false;
        state.actionPlan.fetchingPagination = false;
        state.actionPlan.fetchingSuccessful = true;
        state.actionPlan.fetchingFailed = false;
        state.actionPlan.error = false;
        state.actionPlan.errorText = '';

        if (lastSeenArg) {
          state.actionPlan.items = Array.isArray(items)
            ? state.actionPlan.items
              ? state.actionPlan.items.concat(items)
              : items
            : state.actionPlan.items;

          state.actionPlan.emptyList =
            Array.isArray(items) &&
            (items.length === 0 || items.length % 4 > 0);
        } else {
          state.actionPlan.items = items;
          state.actionPlan.emptyList = false;
        }

        state.actionPlan.lastSeen = lastSeen || state.actionPlan.lastSeen;
      })
      .addCase(fetchActionPlan.rejected, (state, action) => {
        state.actionPlan.fetching = false;
        state.actionPlan.fetchingSuccessful = false;
        state.actionPlan.fetchingPagination = false;
        state.actionPlan.fetchingFailed = true;
        state.actionPlan.error = true;
        state.actionPlan.errorText = action.payload || 'Failed';
      });

    // --- Action History ---
    builder
      .addCase(fetchActionHistory.pending, (state) => {
        state.actionHistory.fetching = true;
        state.actionHistory.fetchingSuccessful = null;
        state.actionHistory.fetchingFailed = false;
        state.actionHistory.error = false;
      })
      .addCase(fetchActionHistory.fulfilled, (state, action) => {
        const { items } = action.payload;
        state.actionHistory.fetching = false;
        state.actionHistory.fetchingSuccessful = true;
        state.actionHistory.fetchingFailed = false;
        state.actionHistory.error = false;
        state.actionHistory.errorText = '';
        state.actionHistory.items = Array.isArray(items) ? items : [];
        state.actionHistory.emptyList =
          !Array.isArray(items) || items.length === 0;
      })
      .addCase(fetchActionHistory.rejected, (state, action) => {
        state.actionHistory.fetching = false;
        state.actionHistory.fetchingSuccessful = false;
        state.actionHistory.fetchingFailed = true;
        state.actionHistory.error = true;
        state.actionHistory.errorText = action.payload || 'Failed';
      });

    // --- Deal Activities ---
    builder
      .addCase(fetchDealActivities.pending, (state, action) => {
        const { lastSeen } = action.meta.arg;
        if (lastSeen) {
          state.dealActivities.fetching = false;
          state.dealActivities.fetchingPagination = true;
        } else {
          state.dealActivities.fetching = true;
          state.dealActivities.fetchingPagination = false;
        }
      })
      .addCase(fetchDealActivities.fulfilled, (state, action) => {
        const { data, lastSeenArg, responseDataLastSeen } = action.payload;
        state.dealActivities.fetching = false;
        state.dealActivities.fetchingPagination = false;
        state.dealActivities.fetchingSuccessful = true;
        state.dealActivities.fetchingFailed = false;
        state.dealActivities.error = false;
        state.dealActivities.errorText = '';

        if (lastSeenArg) {
          state.dealActivities.dealActivities = Array.isArray(data)
            ? state.dealActivities.dealActivities
              ? state.dealActivities.dealActivities.concat(data)
              : data
            : state.dealActivities.dealActivities;

          state.dealActivities.emptyList =
            Array.isArray(data) && (data.length === 0 || data.length % 10 > 0);
        } else {
          state.dealActivities.dealActivities = data;
          state.dealActivities.emptyList = false;
        }

        state.dealActivities.lastSeen =
          responseDataLastSeen || state.dealActivities.lastSeen;
      })
      .addCase(fetchDealActivities.rejected, (state, action) => {
        state.dealActivities.fetching = false;
        state.dealActivities.fetchingSuccessful = false;
        state.dealActivities.fetchingPagination = false;
        state.dealActivities.fetchingFailed = true;
        state.dealActivities.error = true;
        state.dealActivities.errorText = action.payload || 'Failed';
      });

    // --- Deal Artifacts ---
    builder
      .addCase(fetchDealArtifacts.pending, (state) => {
        state.dealArtifacts.fetching = true;
        state.dealArtifacts.fetchingSuccessful = false;
        state.dealArtifacts.fetchingFailed = false;
        state.dealArtifacts.error = false;
        state.dealArtifacts.errorText = '';
      })
      .addCase(fetchDealArtifacts.fulfilled, (state, action) => {
        state.dealArtifacts.fetching = false;
        state.dealArtifacts.fetchingSuccessful = true;
        state.dealArtifacts.fetchingFailed = false;
        state.dealArtifacts.error = false;
        state.dealArtifacts.errorText = '';
        state.dealArtifacts.dealArtifacts = action.payload;

        // Calculate timelineYears and timelineMonths
        const data = action.payload;
        state.dealArtifacts.timelineYears =
          Array.from(
            new Set(
              data?.map((d) => new Date(d?.interactionTime)?.getFullYear()),
            ),
          ).sort((a, b) => b - a) || [];

        state.dealArtifacts.timelineMonths =
          Array.from(
            new Set(
              data?.map((d) => new Date(d?.interactionTime)?.getFullYear()),
            ),
          )?.reduce((acc, curr) => {
            if (acc[curr] === undefined) {
              acc[curr] = Array.from(
                new Set(
                  data
                    ?.filter(
                      (d) =>
                        new Date(d?.interactionTime)?.getFullYear() === curr,
                    )
                    ?.map(
                      (d) => months[new Date(d?.interactionTime)?.getMonth()],
                    ),
                ),
              ).sort((a, b) => months.indexOf(b) - months.indexOf(a));
            }
            return acc;
          }, {}) || {};
      })
      .addCase(fetchDealArtifacts.rejected, (state, action) => {
        state.dealArtifacts.fetching = false;
        state.dealArtifacts.fetchingSuccessful = false;
        state.dealArtifacts.fetchingFailed = true;
        state.dealArtifacts.error = true;
        state.dealArtifacts.errorText = action.payload || 'Failed';
      })
      .addCase(deleteDealActivity.fulfilled, (state, action) => {
        state.dealActivities.dealActivities = state.dealActivities.dealActivities.filter(
          (ac) => ac?.id !== action.payload,
        );
      })
      .addCase(fetchDealById.pending, (state, action) => {
        // Opening a different deal: drop the one on screen straight away. It
        // used to survive until the new deal resolved, so every tab that
        // reads `dealById.deal` (the org chart, Sales Motion, About Company,
        // the header) kept rendering the previous deal's content over the
        // deal the user had just opened - most visible on a deal whose
        // account research is still running and that has nothing of its own
        // to show yet.
        const requestedDealId = action?.meta?.arg;
        if (
          requestedDealId &&
          state.dealById.deal?.id &&
          state.dealById.deal.id !== requestedDealId
        ) {
          state.dealById.deal = null;
        }
        state.dealById.fetching = true;
        state.dealById.fetchingSuccessful = false;
        state.dealById.fetchingFailed = false;
        state.dealById.error = false;
        state.dealById.errorText = '';
      })
      .addCase(fetchDealById.fulfilled, (state, action) => {
        state.dealById.fetching = false;
        state.dealById.fetchingSuccessful = true;
        state.dealById.fetchingFailed = false;
        state.dealById.error = false;
        state.dealById.errorText = '';
        state.dealById.deal = action.payload;
      })
      .addCase(fetchDealById.rejected, (state, action) => {
        state.dealById.fetching = false;
        state.dealById.fetchingSuccessful = false;
        state.dealById.fetchingFailed = true;
        state.dealById.error = true;
        state.dealById.errorText = action.payload || 'Failed';
      })
      .addCase(addDealArtifact.pending, (state) => {
        state.addDealArtifactState.processing = true;
        state.addDealArtifactState.done = false;
        state.addDealArtifactState.failed = false;
        state.addDealArtifactState.error = null;
        state.addDealArtifactState.errorText = null;
      })
      .addCase(addDealArtifact.fulfilled, (state) => {
        state.addDealArtifactState.processing = false;
        state.addDealArtifactState.done = true;
        state.addDealArtifactState.failed = false;
        state.addDealArtifactState.error = null;
        state.addDealArtifactState.errorText = null;
      })
      .addCase(addDealArtifact.rejected, (state, action) => {
        state.addDealArtifactState.processing = false;
        state.addDealArtifactState.done = false;
        state.addDealArtifactState.failed = true;
        state.addDealArtifactState.error = true;
        state.addDealArtifactState.errorText = action.payload;
      });

    // --- Add Action Item ---
    builder
      .addCase(addActionItem.pending, (state) => {
        state.addActionItem.processing = true;
        state.addActionItem.done = false;
        state.addActionItem.failed = false;
        state.addActionItem.error = null;
        state.addActionItem.errorText = null;
      })
      .addCase(addActionItem.fulfilled, (state) => {
        state.addActionItem.processing = false;
        state.addActionItem.done = true;
        state.addActionItem.failed = false;
        state.addActionItem.error = null;
        state.addActionItem.errorText = null;
      })
      .addCase(addActionItem.rejected, (state, action) => {
        state.addActionItem.processing = false;
        state.addActionItem.done = false;
        state.addActionItem.failed = true;
        state.addActionItem.error = true;
        state.addActionItem.errorText = action.payload;
      });

    // --- Update NBA ---
    builder
      .addCase(updateNBA.pending, (state) => {
        state.updateNBA.processing = true;
        state.updateNBA.done = false;
        state.updateNBA.failed = false;
        state.updateNBA.error = null;
        state.updateNBA.errorText = null;
      })
      .addCase(updateNBA.fulfilled, (state) => {
        state.updateNBA.processing = false;
        state.updateNBA.done = true;
        state.updateNBA.failed = false;
        state.updateNBA.error = null;
        state.updateNBA.errorText = null;
      })
      .addCase(updateNBA.rejected, (state, action) => {
        state.updateNBA.processing = false;
        state.updateNBA.done = false;
        state.updateNBA.failed = true;
        state.updateNBA.error = true;
        state.updateNBA.errorText = action.payload;
      });

    // --- Update Bulk NBA Due Date ---
    builder
      .addCase(updateBulkNBADueDate.pending, (state) => {
        state.updateBulkNBADueDate.processing = true;
        state.updateBulkNBADueDate.done = false;
        state.updateBulkNBADueDate.failed = false;
        state.updateBulkNBADueDate.error = null;
        state.updateBulkNBADueDate.errorText = null;
      })
      .addCase(updateBulkNBADueDate.fulfilled, (state) => {
        state.updateBulkNBADueDate.processing = false;
        state.updateBulkNBADueDate.done = true;
        state.updateBulkNBADueDate.failed = false;
        state.updateBulkNBADueDate.error = null;
        state.updateBulkNBADueDate.errorText = null;
      })
      .addCase(updateBulkNBADueDate.rejected, (state, action) => {
        state.updateBulkNBADueDate.processing = false;
        state.updateBulkNBADueDate.done = false;
        state.updateBulkNBADueDate.failed = true;
        state.updateBulkNBADueDate.error = true;
        state.updateBulkNBADueDate.errorText = action.payload;
      });

    // --- Generate NBA ---
    builder
      .addCase(generateNBAThunk.pending, (state) => {
        state.generateNBA.processing = true;
        state.generateNBA.done = false;
        state.generateNBA.failed = false;
        state.generateNBA.error = null;
        state.generateNBA.errorText = null;
      })
      .addCase(generateNBAThunk.fulfilled, (state) => {
        state.generateNBA.processing = false;
        state.generateNBA.done = true;
        state.generateNBA.failed = false;
        state.generateNBA.error = null;
        state.generateNBA.errorText = null;
      })
      .addCase(generateNBAThunk.rejected, (state, action) => {
        state.generateNBA.processing = false;
        state.generateNBA.done = false;
        state.generateNBA.failed = true;
        state.generateNBA.error = true;
        state.generateNBA.errorText = action.payload;
      });

    // --- Fetch NBA By Id ---
    builder
      .addCase(fetchNBAById.pending, (state) => {
        state.nbaById.fetching = true;
        state.nbaById.fetchingSuccessful = false;
        state.nbaById.fetchingFailed = false;
        state.nbaById.error = false;
        state.nbaById.errorText = null;
      })
      .addCase(fetchNBAById.fulfilled, (state, action) => {
        state.nbaById.fetching = false;
        state.nbaById.fetchingSuccessful = true;
        state.nbaById.fetchingFailed = false;
        state.nbaById.error = false;
        state.nbaById.errorText = null;
        state.nbaById.data = action.payload;
      })
      .addCase(fetchNBAById.rejected, (state, action) => {
        state.nbaById.fetching = false;
        state.nbaById.fetchingSuccessful = false;
        state.nbaById.fetchingFailed = true;
        state.nbaById.error = true;
        state.nbaById.errorText = action.payload;
      });

    // --- Fetch Interaction Participants ---
    builder
      .addCase(fetchAllInteractionParticipants.pending, (state) => {
        state.interactionParticipants.fetching = true;
        state.interactionParticipants.fetchingSuccessful = false;
        state.interactionParticipants.fetchingFailed = false;
        state.interactionParticipants.error = false;
        state.interactionParticipants.errorText = null;
      })
      .addCase(fetchAllInteractionParticipants.fulfilled, (state, action) => {
        state.interactionParticipants.fetching = false;
        state.interactionParticipants.fetchingSuccessful = true;
        state.interactionParticipants.fetchingFailed = false;
        state.interactionParticipants.error = false;
        state.interactionParticipants.errorText = null;
        state.interactionParticipants.data = action.payload;
      })
      .addCase(fetchAllInteractionParticipants.rejected, (state, action) => {
        state.interactionParticipants.fetching = false;
        state.interactionParticipants.fetchingSuccessful = false;
        state.interactionParticipants.fetchingFailed = true;
        state.interactionParticipants.error = true;
        state.interactionParticipants.errorText = action.payload;
      });

    // --- Fetch Deal Names ---
    builder
      .addCase(fetchDealNames.pending, (state) => {
        state.dealNames.fetching = true;
        state.dealNames.fetchingSuccessful = false;
        state.dealNames.fetchingFailed = false;
        state.dealNames.error = false;
        state.dealNames.errorText = null;
      })
      .addCase(fetchDealNames.fulfilled, (state, action) => {
        state.dealNames.fetching = false;
        state.dealNames.fetchingSuccessful = true;
        state.dealNames.fetchingFailed = false;
        state.dealNames.error = false;
        state.dealNames.errorText = null;
        state.dealNames.data = action.payload;
      })
      .addCase(fetchDealNames.rejected, (state, action) => {
        state.dealNames.fetching = false;
        state.dealNames.fetchingSuccessful = false;
        state.dealNames.fetchingFailed = true;
        state.dealNames.error = true;
        state.dealNames.errorText = action.payload;
      });

    // --- Fetch Deal Exists Info ---
    builder
      .addCase(fetchDealExistsInfo.pending, (state) => {
        state.dealExistsInfo.fetching = true;
        state.dealExistsInfo.fetchingSuccessful = false;
        state.dealExistsInfo.fetchingFailed = false;
        state.dealExistsInfo.error = false;
        state.dealExistsInfo.errorText = null;
      })
      .addCase(fetchDealExistsInfo.fulfilled, (state, action) => {
        state.dealExistsInfo.fetching = false;
        state.dealExistsInfo.fetchingSuccessful = true;
        state.dealExistsInfo.fetchingFailed = false;
        state.dealExistsInfo.error = false;
        state.dealExistsInfo.errorText = null;
        state.dealExistsInfo.data = action.payload;
      })
      .addCase(fetchDealExistsInfo.rejected, (state, action) => {
        state.dealExistsInfo.fetching = false;
        state.dealExistsInfo.fetchingSuccessful = false;
        state.dealExistsInfo.fetchingFailed = true;
        state.dealExistsInfo.error = true;
        state.dealExistsInfo.errorText = action.payload;
      });

    // --- Candidate thunks ---
    const addM = (t, k) =>
      builder
        .addCase(t.pending, (s) => {
          s[k].processing = true;
          s[k].done = false;
          s[k].failed = false;
        })
        .addCase(t.fulfilled, (s) => {
          s[k].processing = false;
          s[k].done = true;
          s[k].failed = false;
        })
        .addCase(t.rejected, (s, a) => {
          s[k].processing = false;
          s[k].done = false;
          s[k].failed = true;
          s[k].error = a.payload;
        });
    const addF = (t, k) =>
      builder
        .addCase(t.pending, (s) => {
          s[k].fetching = true;
          s[k].fetchingSuccessful = false;
          s[k].fetchingFailed = false;
        })
        .addCase(t.fulfilled, (s, a) => {
          s[k].fetching = false;
          s[k].fetchingSuccessful = true;
          s[k].fetchingFailed = false;
          s[k].data = a.payload;
        })
        .addCase(t.rejected, (s) => {
          s[k].fetching = false;
          s[k].fetchingSuccessful = false;
          s[k].fetchingFailed = true;
        });

    addM(archiveDealsThunk, 'archiveDeals');
    addM(createNewDealThunk, 'createNewDeal');
    addM(deleteActivitiesForDealThunk, 'deleteActivitiesForDeal');
    addM(deleteActivityForDealThunk, 'deleteActivityForDeal');
    addM(deleteDealThunk, 'deleteDeal');
    addM(updateDealThunk, 'updateDeal');
    addM(updateMeetingPrepDataThunk, 'updateMeetingPrepData');
    addM(requestNewCrmThunk, 'requestNewCrm');
    addF(fetchCrmDealById, 'crmDealById');
    addF(fetchDealHighlightContainers, 'dealHighlightContainers');
    addF(fetchDealHighlights, 'dealHighlights');
    addF(fetchDealMeetingPrep, 'dealMeetingPrep');
    addF(fetchMeetingPrepByLinkToken, 'meetingPrepByLinkToken');
    addF(fetchMeetingPrepPublicLink, 'meetingPrepPublicLink');
    addF(fetchDealsByIds, 'dealsByIds');
  },
});

export const {
  resetDealsByIds,
  updateDeals,
  updatePinnedDeals,
  removeDealFromPinnedDeals,
  addDealsToPinnedDeals,
  updateActionPlanItems,
  updateMultipleActionPlanItemsDueDate,
  updateActionPlanItemsList,
  updateActionPlanOrder,
  updateLastSeen,
  addingDealArtifact,
  setUploadArtifactMediaProgress,
  setUploadingArtifactMediaFor,
  addingDealArtifactDone,
  addingDealArtifactFailed,
  updateDealActivity,
  updateDealById,
  updateDealCompany,
  addToActionHistory,
  removeFromActionHistory,
} = dealsSlice.actions;

export default dealsSlice.reducer;
